import { useEffect, useState } from 'react';
import { recoveryApi } from '../services/recoveryApi';
import { HELP_CONTENT_FALLBACK, type HelpContent, type HelpTopic, type HelpTopicKey } from '../types/LegalCase';

// Cache em memória de módulo: o conteúdo é estático, então uma busca por sessão basta. Todo painel
// que abrir depois lê daqui, sem nova chamada. Começa com o fallback local para a tela nunca
// abrir vazia; a resposta do backend sobrescreve quando chega.
let cache: HelpContent = HELP_CONTENT_FALLBACK;
let inFlight: Promise<void> | null = null;

let loaded = false;

const loadHelp = (): Promise<void> => {
    // Já carregado com sucesso: nada a fazer. Uma falha anterior NÃO trava a busca — deixa
    // `inFlight` limpo para um próximo mount tentar de novo (rede transitória não deve desligar a
    // ajuda para sempre).
    if (loaded) return Promise.resolve();
    if (inFlight) return inFlight;
    inFlight = recoveryApi
        .getHelp()
        .then((res) => {
            if (res?.topics && typeof res.topics === 'object') {
                // Mescla sobre o fallback: chave que o backend não mandar continua vindo do local.
                cache = { ...HELP_CONTENT_FALLBACK, ...res.topics };
            }
            loaded = true;
        })
        .catch(() => {
            // silencioso — o fallback local já preenche o painel; permite nova tentativa depois
        })
        .finally(() => {
            inFlight = null;
        });
    return inFlight;
};

/**
 * Ajuda contextual das telas do recovery ("manual vivo" — Task 21). Devolve o tópico pedido, com
 * `HelpTopicKey` tipado para o `tsc` reprovar chave inexistente. Busca o registry uma vez por
 * sessão e cai no fallback local se a rede falhar.
 */
export default function useRecoveryHelp(topic: HelpTopicKey): HelpTopic | null {
    const [content, setContent] = useState<HelpContent>(cache);

    useEffect(() => {
        let active = true;
        loadHelp().then(() => {
            if (active) setContent({ ...cache });
        });
        return () => {
            active = false;
        };
    }, []);

    return content[topic] ?? HELP_CONTENT_FALLBACK[topic] ?? null;
}
